import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { TaskRow } from "@/types";
import { TaskDrawer } from "./TaskDrawer";
import { TaskBoard } from "./TaskBoard";
import "./tasks-dashboard.css";

/**
 * Standalone Kanban page (`/staff/tasks/board`) — the "Board" sub-view of Tasks.
 * The List view stays at /staff/tasks; the nav sub-menu switches between them.
 * An optional `?job=<id>` query scopes the board to one job (e.g. the drawing
 * set card's "Open task board" link). Shares the cross-job TaskDrawer for create
 * (nav "New task") + edit (card click); a save bumps `boardToken` so the board
 * refetches.
 */
export function TasksBoardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [drawer, setDrawer] = useState<TaskRow | "new" | null>(null);
  const [boardToken, setBoardToken] = useState(0);

  const jobParam = searchParams.get("job");
  const jobId = jobParam && Number.isFinite(Number(jobParam)) ? Number(jobParam) : undefined;

  // The nav "New task" button routes here with { create: true }.
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      setDrawer("new");
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  return (
    <section className="tdash">
      <header className="tdash__head">
        <div>
          <h1>Tasks</h1>
          <p className="tdash__subtitle">
            {jobId != null ? (
              <>Scoped to one job · <Link to="/staff/tasks/board">View all</Link></>
            ) : (
              "Drag a card between lanes to update its status"
            )}
          </p>
        </div>
      </header>

      <TaskBoard fill jobId={jobId} reloadToken={boardToken} onOpenTask={(row) => setDrawer(row)} />

      {drawer ? (
        <TaskDrawer
          task={drawer === "new" ? null : drawer}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); setBoardToken((n) => n + 1); }}
        />
      ) : null}
    </section>
  );
}
